import type { Notification } from "@agentsaas/protocol";
import { stripAssetRefs } from "../assets/refs";

/**
 * 通知的纯逻辑（与控制面 notify 模块的约定一致）：
 *   code 是完整翻译 key：notification.<category>.<event>；标题 key = notification.title.<category>；两者都用 params 做 ICU 渲染。
 *   Notification 本身没有 category 字段，从 code 的第二段取。
 */

export const NOTIFY_CATEGORIES = ["approval", "question", "mention", "task_done", "pr_review", "asset_ready", "meeting", "budget", "agent_error"] as const;
export type NotifyCategory = (typeof NOTIFY_CATEGORIES)[number];
export type NotifyPref = "push" | "inbox" | "off";
export const NOTIFY_PREFS: NotifyPref[] = ["push", "inbox", "off"];

export function categoryOf(code: string): NotifyCategory | null {
  const m = /^notification\.([a-z_]+)\./.exec(code);
  const c = m?.[1];
  return c && (NOTIFY_CATEGORIES as readonly string[]).includes(c) ? (c as NotifyCategory) : null;
}

type T = (key: string, params?: Record<string, unknown>) => string;

/** 渲染标题与正文；缺翻译时退回 code，不抛错 */
export function renderNotification(t: T, n: { code: string; params: Notification["params"] | Record<string, unknown> }, exists: (key: string) => boolean): { title: string; body: string; category: NotifyCategory | null } {
  // P8：提问等通知的参数里可能带资产引用（asset://…），标题 / 正文里不显示这串 id
  n = { ...n, params: Object.fromEntries(Object.entries(n.params).map(([k, v]) => [k, typeof v === "string" && v.includes("asset://") ? stripAssetRefs(v) : v])) };
  const category = categoryOf(n.code);
  const titleKey = category ? `notification.title.${category}` : null;
  const title = titleKey && exists(titleKey) ? t(titleKey, n.params) : t("notify.title_generic");
  const body = exists(n.code) ? t(n.code, n.params) : n.code;
  return { title, body, category };
}

/** 只允许站内路径（防止通知 link 被用来跳到外站） */
export function safeLink(link: string | null | undefined): string | null {
  if (!link || !link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  return link;
}

export const CATEGORY_ICON: Record<NotifyCategory, "shield" | "help" | "chat" | "check" | "pr" | "image" | "users" | "key" | "bell"> = {
  approval: "shield",
  question: "help",
  mention: "chat",
  task_done: "check",
  pr_review: "pr",
  asset_ready: "image",
  meeting: "users",
  budget: "key",
  agent_error: "bell",
};
